// Cost per 1M tokens (USD) by model
// Includes both llm.yml IDs (e.g. claude-haiku-4.5) and RubyLLM runtime IDs
// (e.g. claude-haiku-4-5-20251001) so lookups work regardless of source.
export const COST_TABLE = {
  'gpt-4o': { input: 2.5, output: 10.0 },
  'gpt-4o-mini': { input: 0.15, output: 0.6 },
  'gpt-4.1': { input: 2.0, output: 8.0 },
  'gpt-4.1-mini': { input: 0.4, output: 1.6 },
  'gpt-4.1-nano': { input: 0.1, output: 0.4 },
  'gpt-5-mini': { input: 0.4, output: 1.6 },
  'gpt-5-nano': { input: 0.1, output: 0.4 },
  'gpt-5.1': { input: 5.0, output: 20.0 },
  'gpt-5.2': { input: 5.0, output: 20.0 },
  'gpt-3.5-turbo': { input: 0.5, output: 1.5 },
  // Claude — llm.yml IDs
  'claude-haiku-4.5': { input: 0.8, output: 4.0 },
  'claude-sonnet-4.5': { input: 3.0, output: 15.0 },
  // Claude — RubyLLM runtime IDs (logged in usage_logs.model_used)
  'claude-sonnet-4-20250514': { input: 3.0, output: 15.0 },
  'claude-haiku-4-5-20251001': { input: 0.8, output: 4.0 },
  // Gemini
  'gemini-2.0-flash': { input: 0.1, output: 0.4 },
  'gemini-2.5-flash': { input: 0.15, output: 0.6 },
  'gemini-3-flash': { input: 0.1, output: 0.4 },
  'gemini-3-pro': { input: 1.25, output: 5.0 },
};

// Fallback price when model is not in the table
const FALLBACK_PRICE = { input: 1.0, output: 3.0 };

// Assumed input/output ratio when only total_tokens is available
// Typical LLM usage: ~30% input, ~70% output
const INPUT_RATIO = 0.3;
const OUTPUT_RATIO = 0.7;

/**
 * Calculate cost for a model given separate input/output token counts.
 */
export function calculateCost(model, inputTokens, outputTokens) {
  const prices = COST_TABLE[model] || FALLBACK_PRICE;
  return (
    (inputTokens * prices.input) / 1_000_000 +
    (outputTokens * prices.output) / 1_000_000
  );
}

/**
 * Estimate cost when only total_tokens is available (no input/output split).
 * Uses assumed 30/70 ratio.
 */
export function estimateCostFromTotal(model, totalTokens) {
  const inputTokens = Math.round(totalTokens * INPUT_RATIO);
  const outputTokens = totalTokens - inputTokens;
  return calculateCost(model, inputTokens, outputTokens);
}

/**
 * Calculate total cost across multiple models.
 * byModel: { "model_name": total_tokens, ... }
 */
export function calculateTotalCost(byModel) {
  if (!byModel || Object.keys(byModel).length === 0) return 0;
  return Object.entries(byModel).reduce((sum, [model, tokens]) => {
    return sum + estimateCostFromTotal(model, tokens);
  }, 0);
}

/**
 * Format token count for display.
 * 1234567 → "1.2M", 45000 → "45K", 999 → "999"
 */
export function formatTokens(tokens) {
  if (tokens >= 1_000_000) return `${(tokens / 1_000_000).toFixed(1)}M`;
  if (tokens >= 1_000) return `${(tokens / 1_000).toFixed(1)}K`;
  return tokens.toString();
}

/**
 * Format cost as USD string.
 */
export function formatCost(usd) {
  return `$${usd.toFixed(2)}`;
}

/**
 * Format number with locale separators.
 */
export function formatNumber(num) {
  return new Intl.NumberFormat().format(num);
}

// Feature display labels (i18n keys used in components, these are fallback)
export const FEATURE_COLORS = {
  assistant: 'bg-n-blue-9',
  copilot: 'bg-n-violet-9',
  editor: 'bg-n-teal-9',
  insights_passive: 'bg-n-amber-9',
  insights_active: 'bg-n-ruby-9',
  label_suggestion: 'bg-n-jade-9',
  help_center_search: 'bg-n-iris-9',
  audio_transcription: 'bg-n-orange-9',
  image_analysis: 'bg-n-pink-9',
};
